import identity from 'lodash/identity'
import { eventChannel } from 'redux-saga'
import { cancel, delay, fork, put, take, takeEvery, takeLatest, race, select } from 'redux-saga/effects'
import {createActions, handleActions} from 'redux-actions'
import { fromJS, Map } from 'immutable'
import createWebsocketChannel from '../createWebsocketChannel'

export const actions = createActions({
  main: {
    BLUR: identity,
    CONNECTED: identity,
    DISCONNECTED: identity,
    FOCUS: identity,
    RECEIVED_MESSAGE: identity,
    SEND_MESSAGE: identity,
    SET_API_KEY: identity,
    STORE_API_KEY: identity,
    TOGGLE_GPIO: identity
  }
}).main

export default handleActions({
  [actions.connected]: state => ({
    ...state,
    connected: true,
    disconnectCount: 0,
    modal: state.modal.set('show', false)
  }),
  [actions.disconnected]: state => {
    const modal = state.modal
      .set('show', true)
      .set('message', state.connected ? 'Verbindung verloren... Bitte warten' : 'Verbinde... Bitte warten')
    return {
      ...state,
      connected: false,
      disconnectCount: state.disconnectCount + 1,
      modal: state.disconnectCount < 15 ? modal : modal.set('showChangeApiKey', true)
    }
  },
  [actions.receivedMessage]: (state, { payload: { method, payload } }) => {
    switch (method) {
      case 'status':
        return {
          ...state,
          pendingGpioState: false,
          gpioState: fromJS(payload)
        }
      default:
        console.error('unhandled message from server')
    }
    return state
  },
  [actions.setApiKey]: (state, { payload: { apiKey } }) => ({
    ...state,
    apiKey
  }),
  [actions.storeApiKey]: (state, { payload: { apiKey } }) => ({
    ...state,
    apiKey,
    modal: state.modal.set('showChangeApiKey', false)
  }),
  [actions.toggleGpio]: state => ({
    ...state,
    pendingGpioState: true
  }),
  default: identity
}, {
  apiKey: null,
  connected: false,
  disconnectCount: 0,
  gpioState: Map(),
  pendingGpioState: false,
  modal: fromJS({
    show: true,
    message: 'Verbinde... Bitte warten'
  })
})


export function storeApiKeySaga({ payload: { apiKey } }) {
  if (apiKey) localStorage.setItem('mpi_apikey', apiKey)
  else localStorage.removeItem('mpi_apikey')
}

export function * watchConnectionSaga () {
  const { apiKey } = yield select(state => state.main)
  const { channel, write } = createWebsocketChannel(apiKey)
  const watchSendRequests = yield takeEvery(actions.sendMessage, function onSend({ payload: { method, payload }}) {
    write(method, payload)
  })
  try {
    while (true) {
      const { event, payload } = yield take(channel)
      switch (event) {
        case 'open':
          yield put(actions.connected())
          console.log('WS: connected')
          break
        case 'message':
          yield put(actions.receivedMessage(payload))
          break
        default:
          console.error('WS: unknown event from channel')
      }
    }
  } finally {
    yield put(actions.disconnected())
    channel.close()
    watchSendRequests.cancel()
    console.log('WS: disconnected')
  }
}

export function * connectSaga() {
  while (true) {
    let apiKey = localStorage.getItem('mpi_apikey')
    if (!apiKey) {
      console.log('WS: waiting for api key')
      const setApiKeyAction = yield take(actions.storeApiKey)
      apiKey = setApiKeyAction.payload.apiKey
    } else {
      yield put(actions.setApiKey({ apiKey }))
    }
    console.log('WS: connecting...')
    yield fork(watchConnectionSaga)
    yield take(actions.disconnected)
    yield delay(5000)
  }
}

export function * watchVisibilitySaga () {
  const channel = eventChannel(emit => {
    document.addEventListener('visibilitychange', () => emit({hidden: document.hidden}), false)
    return () => {
      document.removeEventListener('visibilitychange')
    }
  })
  while (true) {
    const visibility = yield take(channel)
    yield put(visibility.hidden ? actions.blur() : actions.focus())
  }
}

export function * toggleGpioSaga({ payload: { key } }) {
  const { gpioState } = yield select(state => state.main)
  const nextGpioState = !gpioState.get(key)
  yield put(actions.sendMessage({ method: 'switch', payload: { key, state: nextGpioState } }))
}

export function * monitorConnectionSaga () {
  if (document.hidden) yield take(actions.focus)
  while (true) {
    const connection = yield fork(connectSaga)
    let connected = true
    while (connected) {
      yield take(actions.blur)
      const event = yield race({focus: take(actions.focus), inactivityTimeout: delay(30000)})
      if (event.inactivityTimeout) {
        console.log('WS: closing connection due to inactivity')
        connected = false
        yield cancel(connection)
        yield take(actions.focus)
      }
    }
  }
}

export function * saga () {
  yield fork(monitorConnectionSaga)
  yield fork(watchVisibilitySaga)
  yield takeLatest(actions.storeApiKey, storeApiKeySaga)
  yield takeLatest(actions.toggleGpio, toggleGpioSaga)
}
